import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import api from '../../api';
import PromotionDetail from './PromotionDetail';
import PromotionEditor, {
  hasSaveBlockingPromotionIssues,
} from './PromotionEditor';
import PromotionsList from './PromotionsList';
import {
  emptyDefinition,
  formatUkDateInput,
  parseUkDateInput,
  stablePromotionJson,
  ukDate,
  type PromotionDateInputs,
  type PromotionDefinition,
  type PromotionDetail as PromotionDetailData,
  type PromotionList,
  type PromotionOptions,
  type PromotionPreview,
  type PromotionSummary,
} from './promotionTypes';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const withApiError = (error: unknown, message: string) => {
  const response = isRecord(error) ? error.response : null;
  const data = isRecord(response) ? response.data : null;
  const detail = isRecord(data) ? data.error : null;
  return typeof detail === 'string'
    ? `${message.replace(/\.$/, '')}: ${detail}`
    : message;
};

const PromotionsDashboard: React.FC = () => {
  const { getAccessTokenSilently } = useAuth0();
  const [promotions, setPromotions] = useState<PromotionSummary[]>([]);
  const [selectedSlug, setSelectedSlug] = useState<string | null>(null);
  const [view, setView] = useState<'list' | 'editor' | 'detail'>('list');
  const [detail, setDetail] = useState<PromotionDetailData | null>(null);
  const [options, setOptions] = useState<PromotionOptions | null>(null);
  const [definition, setDefinition] = useState<PromotionDefinition | null>(
    null,
  );
  const [dateInputs, setDateInputs] = useState<PromotionDateInputs | null>(
    null,
  );
  const [preview, setPreview] = useState<PromotionPreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const auth = useCallback(async () => {
    const token = await getAccessTokenSilently();
    return { headers: { Authorization: `Bearer ${token}` } };
  }, [getAccessTokenSilently]);

  const refreshList = useCallback(async () => {
    const result = await api.get<PromotionList>(
      '/promotion-stats/promotions',
      await auth(),
    );
    setPromotions(result.data.promotions);
    return result.data.promotions;
  }, [auth]);

  const refreshDetail = useCallback(
    async (slug: string) => {
      const result = await api.get<PromotionDetailData>(
        `/promotion-stats/promotions/${encodeURIComponent(slug)}`,
        await auth(),
      );
      setDetail(result.data);
      setPromotions((current) =>
        current.map((promotion) =>
          promotion.slug === slug ? result.data.promotion : promotion,
        ),
      );
      return result.data;
    },
    [auth],
  );

  useEffect(() => {
    let active = true;
    void (async () => {
      try {
        const config = await auth();
        const [list, optionResponse] = await Promise.all([
          api.get<PromotionList>('/promotion-stats/promotions', config),
          api.get<PromotionOptions>(
            '/promotion-stats/promotions/options',
            config,
          ),
        ]);
        if (!active) return;
        setPromotions(list.data.promotions);
        setOptions(optionResponse.data);
      } catch {
        if (active) setError('Failed to load promotions');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [auth]);

  const current =
    promotions.find((promotion) => promotion.slug === selectedSlug) ?? null;

  const launchingSlugs = promotions
    .filter((promotion) => promotion.status === 'launching')
    .map((promotion) => promotion.slug)
    .join(',');

  const openPromotionRef = useRef<
    ((promotion: PromotionSummary) => Promise<void>) | null
  >(null);

  const [staleDetailSlug, setStaleDetailSlug] = useState<string | null>(null);

  useEffect(() => {
    const wasLaunching = launchingSlugs ? launchingSlugs.split(',') : [];
    const watched =
      view === 'detail' &&
      selectedSlug &&
      (wasLaunching.includes(selectedSlug) || staleDetailSlug === selectedSlug)
        ? selectedSlug
        : null;
    if (wasLaunching.length === 0 && !watched) return;
    const timer = window.setInterval(() => {
      void (async () => {
        try {
          const result = await api.get<PromotionList>(
            '/promotion-stats/promotions',
            await auth(),
          );
          const list = result.data.promotions;
          setPromotions(list);
          const finished = list.filter(
            (promotion) =>
              wasLaunching.includes(promotion.slug) &&
              promotion.status !== 'launching',
          );
          const next = list.find(
            (promotion) =>
              promotion.slug === watched && promotion.status !== 'launching',
          );
          if (next?.status === 'draft' && !next.codeDefined) {
            await openPromotionRef.current?.(next);
          } else if (next) {
            try {
              await refreshDetail(next.slug);
              setStaleDetailSlug(null);
            } catch {
              setStaleDetailSlug(next.slug);
            }
          }
          const failed = finished.filter(
            (promotion) => promotion.status === 'draft',
          );
          if (failed.length > 0) {
            setNotice(null);
            setError(
              `The launch of ${failed
                .map((promotion) => promotion.name)
                .join(
                  ', ',
                )} didn't finish. Check the draft and start it again.`,
            );
          } else if (
            finished.some((promotion) => promotion.status === 'live')
          ) {
            setNotice('Promotion launched.');
          }
        } catch {
          // The next tick retries.
        }
      })();
    }, 3000);
    return () => window.clearInterval(timer);
  }, [
    launchingSlugs,
    staleDetailSlug,
    selectedSlug,
    view,
    auth,
    refreshDetail,
  ]);

  const startNewPromotion = () => {
    const next = emptyDefinition();
    setSelectedSlug(null);
    setDetail(null);
    setDefinition(next);
    setDateInputs({
      startDate: formatUkDateInput(next.startDate),
      endDate: formatUkDateInput(next.endDate),
    });
    setPreview(null);
    setError(null);
    setNotice(null);
    setView('editor');
  };

  const openPromotion = async (promotion: PromotionSummary) => {
    setSelectedSlug(promotion.slug);
    setDetail(null);
    setDefinition(null);
    setDateInputs(null);
    setPreview(null);
    setError(null);
    setNotice(null);
    setView(
      promotion.status === 'draft' && !promotion.codeDefined
        ? 'editor'
        : 'detail',
    );
    setBusy('detail');
    try {
      const loaded = await refreshDetail(promotion.slug);
      if (promotion.status === 'draft') {
        const next = JSON.parse(
          JSON.stringify(loaded.promotion.definition),
        ) as PromotionDefinition;
        if (next.whatsapp.facts.length > 0) {
          next.whatsapp = {
            ...next.whatsapp,
            assistantNote: [
              next.whatsapp.assistantNote,
              next.whatsapp.facts.join('\n'),
            ]
              .filter(Boolean)
              .join('\n\n'),
            facts: [],
          };
        }
        setDefinition(next);
        setDateInputs({
          startDate: formatUkDateInput(next.startDate),
          endDate: formatUkDateInput(next.endDate),
        });
      }
    } catch {
      setError('Failed to load promotion details');
      setView('list');
    } finally {
      setBusy(null);
    }
  };

  openPromotionRef.current = openPromotion;

  const backToList = () => {
    setView('list');
    setDefinition(null);
    setDateInputs(null);
    setPreview(null);
    setError(null);
    setNotice(null);
  };

  const updateDateInput = (field: keyof PromotionDateInputs, value: string) => {
    setDateInputs((currentInputs) => ({
      startDate:
        currentInputs?.startDate ??
        formatUkDateInput(definition?.startDate ?? ''),
      endDate:
        currentInputs?.endDate ?? formatUkDateInput(definition?.endDate ?? ''),
      [field]: value,
    }));
    const isoDate = parseUkDateInput(value);
    if (isoDate) {
      setDefinition((currentDefinition) =>
        currentDefinition
          ? { ...currentDefinition, [field]: isoDate }
          : currentDefinition,
      );
    }
  };

  const dateValues = {
    startDate:
      dateInputs?.startDate ?? formatUkDateInput(definition?.startDate ?? ''),
    endDate:
      dateInputs?.endDate ?? formatUkDateInput(definition?.endDate ?? ''),
  };
  const hasUnsavedChanges = Boolean(
    definition &&
      (!selectedSlug ||
        (current?.status === 'draft' &&
          (!detail ||
            stablePromotionJson(definition) !==
              stablePromotionJson(detail.promotion.definition) ||
            dateValues.startDate !==
              formatUkDateInput(detail.promotion.definition.startDate) ||
            dateValues.endDate !==
              formatUkDateInput(detail.promotion.definition.endDate)))),
  );

  const save = async () => {
    if (!definition) return;
    const startDate = parseUkDateInput(dateValues.startDate);
    const endDate = parseUkDateInput(dateValues.endDate);
    if (
      hasSaveBlockingPromotionIssues(definition, dateValues) ||
      !startDate ||
      !endDate ||
      endDate < startDate
    ) {
      return;
    }
    const payload: PromotionDefinition = {
      ...definition,
      startDate,
      endDate,
      card: definition.channels.includes('whatsapp') ? definition.card : null,
      briefing: definition.channels.includes('briefing')
        ? definition.briefing
        : null,
    };
    setBusy('save');
    setError(null);
    try {
      let savedSlug = selectedSlug;
      if (selectedSlug && current?.status === 'draft') {
        await api.put(
          `/promotion-stats/promotions/${encodeURIComponent(selectedSlug)}`,
          { definition: payload },
          await auth(),
        );
        setNotice('Draft saved.');
      } else {
        const result = await api.post<{ slug: string }>(
          '/promotion-stats/promotions',
          { definition: payload },
          await auth(),
        );
        savedSlug = result.data.slug;
        setSelectedSlug(savedSlug);
        setNotice('Draft created.');
      }
      setDefinition(payload);
      setDateInputs({
        startDate: formatUkDateInput(payload.startDate),
        endDate: formatUkDateInput(payload.endDate),
      });
      setPreview(null);
      await refreshList();
      if (savedSlug) await refreshDetail(savedSlug);
    } catch (error) {
      setError(
        withApiError(
          error,
          'Could not save the draft. Check the fields and try again.',
        ),
      );
    } finally {
      setBusy(null);
    }
  };

  const previewAudience = async () => {
    if (!definition) return;
    setBusy('preview');
    setError(null);
    try {
      const result = await api.post<PromotionPreview>(
        '/promotion-stats/promotions/preview',
        { definition },
        await auth(),
      );
      setPreview(result.data);
    } catch (error) {
      setError(withApiError(error, 'Could not preview this audience.'));
    } finally {
      setBusy(null);
    }
  };

  const runAction = async (action: 'launch' | 'pause' | 'resume' | 'close') => {
    if (!selectedSlug) return;
    setBusy(action);
    setError(null);
    setNotice(null);
    try {
      if (action === 'launch') {
        const draft = detail?.promotion.definition;
        if (!draft) throw new Error('Promotion details are not loaded');
        if (detail.promotion.codeDefined) {
          if (
            !window.confirm(
              `Start ${detail.promotion.name}? It goes to ${detail.cohortSize ?? 0} people, ${ukDate(draft.startDate)} – ${ukDate(draft.endDate)}. Messages will be scheduled.`,
            )
          ) {
            return;
          }
        } else {
          const audience =
            preview ??
            (
              await api.post<PromotionPreview>(
                '/promotion-stats/promotions/preview',
                { definition: draft },
                await auth(),
              )
            ).data;
          setPreview(audience);
          const estimatedHoldout = Math.round(
            (audience.eligible * draft.holdoutPercent) / 100,
          );
          if (
            !window.confirm(
              draft.afterOnboardingDays === null
                ? `Launch ${draft.name}? ${audience.eligible} eligible users; approximately ${estimatedHoldout} will be in holdout. The audience will be frozen and messages scheduled.`
                : draft.audience.newUsersOnly
                  ? `Launch ${draft.name}? It goes only to new users, ${draft.afterOnboardingDays} days after they first finish onboarding, until ${ukDate(draft.endDate)}. Nobody who has already onboarded gets it.`
                  : `Launch ${draft.name}? It goes to people ${draft.afterOnboardingDays} days after they finish onboarding, until ${ukDate(draft.endDate)}. ${audience.eligible} people match the filters today.`,
            )
          ) {
            return;
          }
        }
      }
      if (action === 'pause' || action === 'resume' || action === 'close') {
        await api.post(
          `/promotion-stats/promotions/${encodeURIComponent(selectedSlug)}/status`,
          {
            status:
              action === 'resume'
                ? 'live'
                : action === 'pause'
                  ? 'paused'
                  : 'closed',
          },
          await auth(),
        );
      } else {
        await api.post(
          `/promotion-stats/promotions/${encodeURIComponent(selectedSlug)}/${action}`,
          {},
          await auth(),
        );
      }
      setNotice(
        action === 'launch'
          ? 'Launching. It moves to Live once everyone is scheduled.'
          : `Promotion ${action === 'pause' ? 'paused' : action === 'resume' ? 'resumed' : 'closed'}.`,
      );
      await Promise.all([refreshList(), refreshDetail(selectedSlug)]);
      if (action === 'launch') {
        setView('detail');
        setDefinition(null);
        setDateInputs(null);
      }
    } catch (error) {
      setError(withApiError(error, `Could not ${action} this promotion.`));
    } finally {
      setBusy(null);
    }
  };

  const extendEndDate = async (endDate: string) => {
    if (!selectedSlug) return;
    setBusy('extend');
    setError(null);
    setNotice(null);
    try {
      const result = await api.post<{ rescheduled: number }>(
        `/promotion-stats/promotions/${encodeURIComponent(selectedSlug)}/end-date`,
        { endDate },
        await auth(),
      );
      const { rescheduled } = result.data;
      setNotice(
        `End date moved to ${ukDate(endDate)}.${
          rescheduled > 0
            ? ` ${rescheduled} skipped ${rescheduled === 1 ? 'send was' : 'sends were'} rescheduled.`
            : ''
        }`,
      );
      await Promise.all([refreshList(), refreshDetail(selectedSlug)]);
    } catch (extendError) {
      setError(withApiError(extendError, 'Could not extend this promotion.'));
    } finally {
      setBusy(null);
    }
  };

  const handleCardSubmitted = async () => {
    if (selectedSlug) {
      await Promise.all([refreshList(), refreshDetail(selectedSlug)]);
    }
  };

  const saveWhatsNewSettings = async (settings: {
    startDate: string;
    endDate: string;
    weekdays: number[];
    partsOfDay: Array<'morning' | 'afternoon' | 'evening'>;
  }) => {
    if (!selectedSlug) return;
    setBusy('settings');
    setError(null);
    setNotice(null);
    try {
      await api.put(
        `/promotion-stats/promotions/${encodeURIComponent(selectedSlug)}/settings`,
        settings,
        await auth(),
      );
      setNotice('Settings saved.');
      const [loaded] = await Promise.all([
        refreshDetail(selectedSlug),
        refreshList(),
      ]);
      setDefinition(loaded.promotion.definition);
      setDateInputs({
        startDate: formatUkDateInput(loaded.promotion.definition.startDate),
        endDate: formatUkDateInput(loaded.promotion.definition.endDate),
      });
    } catch (saveError) {
      setError(withApiError(saveError, 'Could not save the settings.'));
    } finally {
      setBusy(null);
    }
  };

  const refreshWhatsNewCardStatuses = async () => {
    if (!selectedSlug) return;
    setBusy('refresh');
    setError(null);
    try {
      await api.post(
        `/promotion-stats/promotions/${encodeURIComponent(selectedSlug)}/card-status`,
        {},
        await auth(),
      );
      await refreshDetail(selectedSlug);
    } catch (refreshError) {
      setError(
        withApiError(refreshError, 'Could not refresh template status.'),
      );
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className='launches-dashboard promotions-dashboard'>
      {error && (
        <div className='launch-error' role='alert'>
          {error}
        </div>
      )}
      {notice && (
        <div className='launch-success' role='status'>
          {notice}
        </div>
      )}
      {view === 'list' && (
        <PromotionsList
          promotions={promotions}
          sourceLabels={Object.fromEntries(
            (options?.sources ?? []).map(({ key, label }) => [key, label]),
          )}
          loading={loading}
          onCreate={startNewPromotion}
          onOpen={(promotion) => void openPromotion(promotion)}
        />
      )}
      {view === 'editor' &&
        (definition ? (
          <PromotionEditor
            definition={definition}
            dateInputs={dateValues}
            options={options}
            detail={detail}
            current={current}
            selectedSlug={selectedSlug}
            preview={preview}
            busy={busy}
            hasUnsavedChanges={hasUnsavedChanges}
            onChange={setDefinition}
            onDateInputChange={updateDateInput}
            onSave={() => void save()}
            onLaunch={() => void runAction('launch')}
            onCheckAudience={() => void previewAudience()}
            onBack={backToList}
            auth={auth}
            onCardSubmitted={handleCardSubmitted}
          />
        ) : (
          <p className='launch-hint'>
            {busy === 'detail' ? 'Loading draft…' : 'Choose a draft to edit.'}
          </p>
        ))}
      {view === 'detail' &&
        (detail ? (
          <PromotionDetail
            detail={detail}
            busy={busy}
            onBack={backToList}
            onAction={(action) => void runAction(action)}
            onStart={() => void runAction('launch')}
            onExtend={(endDate) => void extendEndDate(endDate)}
            onSaveSettings={(settings) => void saveWhatsNewSettings(settings)}
            onRefreshCardStatus={() => void refreshWhatsNewCardStatuses()}
            auth={auth}
            onCardSubmitted={handleCardSubmitted}
          />
        ) : (
          <p className='launch-hint'>
            {busy === 'detail' ? 'Loading promotion…' : 'Choose a promotion.'}
          </p>
        ))}
    </div>
  );
};

export default PromotionsDashboard;
